import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { colors, fonts } from '../brand';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

export default function PrivacyPolicy() {

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Privacy Policy — Elephants Answering</title>
        <meta name="description" content="How Elephants Answering collects, uses, and protects your personal information." />
      </Helmet>
      <SiteNav />

      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <h1 style={styles.heroTitle}>Privacy Policy</h1>
          <p style={styles.heroSub}>Last updated: April 29, 2026</p>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.inner}>

          <p style={styles.body}>
            Elephants Answering, PBC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard information when you use our website at <strong>elephantsanswering.com</strong> and any related services (collectively, the "Service").
          </p>
          <p style={styles.body}>
            By using the Service, you agree to the collection and use of information as described in this policy.
          </p>

          <h2 style={styles.heading}>1. Information We Collect</h2>
          <p style={styles.body}><strong>Account information.</strong> When you register, we collect your name and email address.</p>
          <p style={styles.body}><strong>Quiz responses.</strong> To generate your roadmap, we collect answers to onboarding questions about your situation (such as your general persona, whether you have children, and your state of residence). We do not collect the identity of your spouse or any third party.</p>
          <p style={styles.body}><strong>Payment information.</strong> Subscription payments are processed by Stripe. We do not store your credit card number or full payment details on our servers. Stripe's privacy policy governs how your payment information is handled.</p>
          <p style={styles.body}><strong>Contact messages.</strong> If you contact us through the site, we collect the content of your message, your name, and your email address.</p>
          <p style={styles.body}><strong>Usage data.</strong> We may collect standard technical information such as browser type, IP address, and pages visited, to help us improve the Service.</p>

          <h2 style={styles.heading}>2. How We Use Your Information</h2>
          <p style={styles.body}>We use the information we collect to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Create and manage your account</li>
            <li style={styles.listItem}>Generate and display your divorce roadmap</li>
            <li style={styles.listItem}>Process subscription payments through Stripe</li>
            <li style={styles.listItem}>Respond to your messages and support requests</li>
            <li style={styles.listItem}>Improve and maintain the Service</li>
            <li style={styles.listItem}>Send important account or service-related communications</li>
          </ul>
          <p style={styles.body}>We do not sell your personal information to third parties.</p>

          <h2 style={styles.heading}>3. Data Storage and Security</h2>
          <p style={styles.body}>
            Your data is stored on secure servers. We use industry-standard measures to protect your information, including encrypted connections (HTTPS). While we take reasonable precautions, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 style={styles.heading}>4. Third-Party Services</h2>
          <p style={styles.body}>We use the following third-party services that may process your data:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Stripe</strong> — payment processing. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>stripe.com/privacy</a>.</li>
            <li style={styles.listItem}><strong>Vercel</strong> — website hosting.</li>
            <li style={styles.listItem}><strong>Railway</strong> — backend infrastructure.</li>
          </ul>

          <h2 style={styles.heading}>5. Sensitive Information</h2>
          <p style={styles.body}>
            We understand that the information you share with us relates to a difficult and private situation. We treat everything you share with the utmost care and discretion. We will never use your responses to contact you for marketing purposes unrelated to the Service, and we will never share identifying information about your situation with any third party without your explicit consent, except as required by law.
          </p>

          <h2 style={styles.heading}>6. Your Rights</h2>
          <p style={styles.body}>You may request to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Access the personal data we hold about you</li>
            <li style={styles.listItem}>Correct inaccurate data</li>
            <li style={styles.listItem}>Delete your account and associated data</li>
          </ul>
          <p style={styles.body}>To make any of these requests, please contact us at the email below.</p>

          <h2 style={styles.heading}>7. Children's Privacy</h2>
          <p style={styles.body}>
            The Service is intended for adults. We do not knowingly collect personal information from anyone under the age of 18. If you believe a minor has provided us with personal data, please contact us and we will remove it promptly.
          </p>

          <h2 style={styles.heading}>8. Changes to This Policy</h2>
          <p style={styles.body}>
            We may update this Privacy Policy from time to time. We will notify registered users of material changes by email or by posting a notice on the site. Your continued use of the Service after changes are posted constitutes your acceptance of the updated policy.
          </p>

          <h2 style={styles.heading}>9. Contact Us</h2>
          <p style={styles.body}>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p style={styles.body}>
            <strong>Elephants Answering, PBC</strong><br />
            <Link to="/contact" style={styles.contactLink}>elephantsanswering.com/contact</Link>
          </p>

        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: colors.cream, fontFamily: fonts.body },

  hero: { backgroundColor: colors.navy, padding: '64px 40px 48px' },
  heroInner: { maxWidth: '760px', margin: '0 auto' },
  heroTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '32px', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '10px' },
  heroSub: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.5)', fontSize: '13px', letterSpacing: '0.3px' },

  section: { padding: '64px 40px' },
  inner: { maxWidth: '760px', margin: '0 auto' },

  heading: { fontFamily: fonts.heading, color: colors.navy, fontSize: '17px', fontWeight: '600', letterSpacing: '0.3px', marginTop: '40px', marginBottom: '12px' },
  body: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.9', marginBottom: '16px' },
  list: { paddingLeft: '20px', marginBottom: '16px' },
  listItem: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '2', marginBottom: '4px' },
  link: { color: colors.rose, textDecoration: 'underline' },
  contactLink: { color: colors.rose, fontFamily: fonts.body, fontSize: '14px', textDecoration: 'underline' },
};
